import React, { useState, useCallback, useEffect } from 'react';

import dashboardApi from 'services/apis/dashboard';
import { Table, Text, Link, Button } from 'v3/components';
import { formatDateTime } from 'v3/utils/formatter';
import moment from 'moment';
import { useQuery } from 'hooks';
import Card, { Number, Description, SeeAll } from './Card';

export default function TravelAdjustments({ dashboard, name, id }) {
  const [isFetching, setFetching] = useState(false);
  const [data, setData] = useState({});
  const [filter, setFilter] = useState({});
  const [currentPage, setCurrentPage] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [daysFromNow, setDaysFromNow] = useState(3);
  const queryParams = useQuery();

  useEffect(() => {
    const startDate = moment().subtract(filter.start_date, 'days');
    setDaysFromNow(moment().diff(startDate, 'days'));
  }, [filter.start_date]);

  function setQueryParams(filters) {
    const { start_date, end_date } = filters;

    if (start_date) {
      queryParams.set('start_date', start_date);
    }

    if (end_date) {
      queryParams.set('end_date', end_date);
    }
  }

  const fetchTravelAdjustments = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      const {
        clients = [],
        negotiators = [],
        costCenters = [],
        pcps = [],
        page = 1,
        travelAdjustmentsStatus,
        destinationSector,
        originSector,
        start_date,
        end_date,
      } = filters;

      if (page === 1) {
        setFetching(true);
      }

      setQueryParams(filters);
      setFilter(filters);
      setCurrentPage(+page);

      const response = await dashboardApi.get(`/travel-adjustments`, {
        params: {
          clients: clients?.map(client => client.id),
          start_date: start_date,
          end_date: end_date,
          negotiators: negotiators?.map(negotiator => negotiator?.id),
          originSector: originSector?.value,
          destinationSector: destinationSector?.value,
          costCenters: costCenters?.map(costCenter => costCenter?.id),
          pcps: pcps?.map(pcp => pcp?.id),
          status: travelAdjustmentsStatus?.value,
          page,
        },
      });
      setPagination(response.data?.last?.pagination);

      if (page === 1) {
        setData(response.data);
      } else {
        setData(oldData => ({
          ...oldData,
          last: {
            ...oldData.last,
            data: [...oldData.last.data, ...response.data.last.data],
          },
        }));
      }
    } catch (ex) {
      // Handle exception
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, []);

  return (
    <Card
      title="Pendência de viagens por setor"
      isLoading={isFetching}
      fetchFn={fetchTravelAdjustments}
      onFiltersChanged={fetchTravelAdjustments}
      excludeFilterByCompany={true}
      excludeFilterByTags={true}
      excludeFilterBydestinationSectors={false}
      excludeFilterByoriginSectors={false}
      excludeFilterTravelAdjustmentsStatus={false}
      dashboard={dashboard}
      name={name}
      id={id}
      ListComponent={
        <>
          <Table striped>
            <thead>
              <tr>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Criado de criação
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Número da viagem
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Carga
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Status
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Setor de origem
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Setor de destino
                  </Text>
                </th>
                <th style={{ minWidth: '200px' }}>
                  <Text weight="500" color="dark">
                    Motivo
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Descrição
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Cliente
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Veículo
                  </Text>
                </th>
                <th className="text-nowrap">
                  <Text weight="500" color="dark">
                    Implemento
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.last?.data?.map(travel => {
                const wagonsFormat = travel?.wagon_plates
                  ? travel?.wagon_plates?.split(",").map(wagons => {
                    const [id, plate] = wagons.trim().split("-");
                    return { id, plate };
                  })
                  : [];

                return (
                  <tr>
                    <td className="text-nowrap">
                      <Text type="regular" color="gray">
                        {formatDateTime(travel?.created_at)}
                      </Text>
                    </td>
                    <td className="text-nowrap">
                      <Link
                        to={`/viagens/${travel?.travel_id}`}
                        target="_blank"
                      >
                        {travel?.travel_number}
                      </Link>
                    </td>
                    <td className="text-nowrap">
                      <Link
                        to={`/cargas/${travel?.load_id}`}
                        target="_blank"
                      >
                        {travel?.load_id}
                      </Link>
                    </td>
                    <td className="text-nowrap">
                      <Text type="regular" color="gray">
                        {travel?.status || 'Não informado'}
                      </Text>
                    </td>
                    <td className="text-nowrap">
                      <Text type="regular" color="gray">
                        {travel?.origin_sector || 'Não informado'}
                      </Text>
                    </td>
                    <td className="text-nowrap">
                      <Text type="regular" color="gray">
                        {travel?.destination_sector || 'Não informado'}
                      </Text>
                    </td>
                    <td>
                      <Text type="regular" color="gray">
                        {travel?.reason || 'Não informado'}
                      </Text>
                    </td>
                    <td className="text-nowrap">
                      <Text type="regular" color="gray">
                        {travel?.description || 'Não informado'}
                      </Text>
                    </td>
                    <td className="text-nowrap">
                      <Text type="regular" color="gray">
                        {travel?.taker_name || 'Não informado'}
                      </Text>
                      <br />
                      <Text type="regular" color="gray">
                        {travel?.taker_cgccpf || 'Não informado'}
                      </Text>
                    </td>
                    <td className="text-nowrap">
                      <Text type="regular" color="gray">
                        <Link
                          to={`/veiculos/${travel?.vehicle_id}`}
                          target="_blank"
                          key={travel?.vehicle_id}
                        >
                          {travel?.vehicle_plate}
                        </Link>
                      </Text>
                    </td>
                    <td className="text-nowrap">
                      {wagonsFormat.length > 0 ? (
                        wagonsFormat.map(wagon => (
                          <Link
                            to={`/implementos/${wagon.id}`}
                            target="_blank"
                            key={wagon.id}
                          >
                            {wagon.plate}
                          </Link>
                        ))
                      ) : (
                        <Text type="regular" color="gray">
                          Não informado
                        </Text>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {pagination?.lastPage > 1 && pagination?.to < pagination?.total && (
            <div className="text-center mt-3 col-12">
              <Button
                className="mt-2 ml-auto align-item-center"
                onClick={() => {
                  fetchTravelAdjustments({
                    page: currentPage + 1,
                    clients: filter?.clients,
                    start_date: filter?.start_date,
                    end_date: filter?.end_date,
                    costCenters: filter?.costCenters,
                    pcps: filter?.pcps,
                    negotiators: filter?.negotiators,
                    destinationSector: filter?.destinationSector,
                    originSector: filter?.originSector,
                    travelAdjustmentsStatus: filter?.travelAdjustmentsStatus
                  });
                }}
                loading={loading}
              >
                <Text type="regular" color="white" truncate>
                  Ver mais
                </Text>
              </Button>
            </div>
          )}
          {!!data?.total && (
            <SeeAll
              to={{
                pathname: '/viagens',
                search: queryParams.toString(),
              }}
            >
              ver todas as {data.total} viagens
            </SeeAll>
          )}
        </>
      }
      NumericComponent={
        <div className="d-flex justify-content-center text-center flex-column">
          <Number color="warning">{data?.total}</Number>
          <Description>
            Pendência de viagens por setor
            {daysFromNow > 0 && daysFromNow < 61
              ? ` nos últimos ${daysFromNow} dias`
              : ` no período de  ${moment.utc(filter.start_date).format('DD/MM/YYYY')} até ${moment.utc(filter.end_date).format('DD/MM/YYYY')}`}
          </Description>
        </div>
      }
    />
  );
}
